import type { GameType } from '@hit-game/shared/enums';
import type {
  BulkGroupAction,
  IBulkGroupActionResult,
  IGroup,
  IGroupSessionHistory,
  IHitBulkActionResult,
} from '@hit-game/shared/types';

import { http, type Paginated } from '../lib/http';

export const groupService = {
  list(params?: { page?: number; limit?: number; search?: string }): Promise<Paginated<IGroup>> {
    return http.get('/groups', { params, _silent: true });
  },

  detail(id: string): Promise<IGroup> {
    return http.get(`/groups/${id}`);
  },

  create(input: { name: string; gameType?: GameType }): Promise<IGroup> {
    return http.post('/groups', input);
  },

  update(id: string, input: Partial<IGroup>): Promise<IGroup> {
    return http.put(`/groups/${id}`, input);
  },

  remove(id: string): Promise<null> {
    return http.delete(`/groups/${id}`);
  },

  /**
   * Đăng nhập toàn bộ tài khoản trong nhóm.
   *
   * RẤT chậm (mỗi tài khoản một phiên browser) và luôn trả 200 kèm kết quả từng
   * tài khoản, kể cả khi hỏng hết — đọc `results` để biết cái nào cần bấm lại.
   */
  login(id: string): Promise<{ results: IHitBulkActionResult[] }> {
    return http.post(`/groups/${id}/login`);
  },

  logout(id: string): Promise<{ results: IHitBulkActionResult[] }> {
    return http.post(`/groups/${id}/logout`);
  },

  /**
   * Bật engine đánh hộ: nối socket game cho cả nhóm rồi tự đặt lệnh mỗi phiên.
   *
   * KHÁC `login`: đăng nhập mở phiên với nhà cái, còn đây mở socket game. Dừng
   * engine KHÔNG đăng xuất. Một thành viên nối hỏng thì server gỡ lại cả nhóm và
   * trả lỗi — nhóm chạy nửa vời là vế còn lại cược trần một cửa.
   */
  start(id: string): Promise<{ results: IHitBulkActionResult[] }> {
    return http.post(`/groups/${id}/start`);
  },

  /** Số dư sống của các nhóm đang chạy (`accountId → số dư`). Rẻ, hỏi dày được. */
  liveBalances(): Promise<{ balances: Record<string, number> }> {
    return http.get('/groups/live-balances', { _silent: true });
  },

  stop(id: string): Promise<{
    results: IHitBulkActionResult[];
    /** Đang treo tiền ở một ván nên CHƯA dừng — nhóm đánh nốt ván đó rồi tự dừng. */
    deferred?: boolean;
    sessionLabel?: string | null;
  }> {
    return http.post(`/groups/${id}/stop`);
  },

  restart(id: string): Promise<{ results: IHitBulkActionResult[] }> {
    return http.post(`/groups/${id}/restart`);
  },

  /**
   * Start/stop NHIỀU nhóm một lượt. Server chạy song song có trần và tự BỎ QUA
   * nhóm không đủ điều kiện — `results` báo từng nhóm ok/skipped/error.
   *
   * Chậm (mỗi nhóm nối/gỡ socket cho cả nhóm) nên có thể mất vài giây tới ~10s ở
   * 50 nhóm — lối gọi phải khoá nút trong lúc chờ.
   */
  bulkAction(
    action: BulkGroupAction,
    groupIds: string[],
  ): Promise<{ results: IBulkGroupActionResult[] }> {
    return http.post('/groups/bulk-action', { action, groupIds });
  },

  /**
   * Các phiên gần nhất của nhóm ("Show Phiên").
   *
   * Dựng từ Lịch Sử Cược đã đồng bộ, không chạm nhà cái — engine chỉ biết nó đã
   * GỬI lệnh gì, kết quả ván thì chỉ bet log mới có. Đồng bộ bet log là việc của
   * thẻ tài khoản; ở đây chỉ đọc.
   */
  history(id: string, limit?: number): Promise<IGroupSessionHistory> {
    return http.get(`/groups/${id}/history`, { params: limit === undefined ? {} : { limit } });
  },
};
